import { useCallback, useEffect, useState } from 'react';
import { Columns3, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  getAllMachines,
  createMachine,
  updateMachine,
  deleteMachine,
  getAvailableMachineSerials,
} from '../api/api';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './MachineMgr.module.scss';

// Columns that can be toggled via the checkboxes above the table.
// `defaultVisible: false` columns start hidden.
const TOGGLEABLE_COLUMNS = [
  { key: 'id', label: 'ID', defaultVisible: true },
  { key: 'serial', label: 'Serial Number', defaultVisible: true },
  { key: 'nickname', label: 'Nickname', defaultVisible: true },
  { key: 'companyName', label: 'Company', defaultVisible: true },
  { key: 'ntripUser', label: 'NTRIP User', defaultVisible: true },
  { key: 'installDate', label: 'Install Date', defaultVisible: false },
  { key: 'iccid', label: 'ICCID', defaultVisible: false },
  { key: 'imei', label: 'IMEI', defaultVisible: false },
  { key: 'gnssVersion', label: 'GNSS Version', defaultVisible: false },
];

function MachineMgr() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [pendingDeleteMachine, setPendingDeleteMachine] = useState(null);
  const [availableSerials, setAvailableSerials] = useState([]);

  // Column visibility + sorting state
  const [visibleColumns, setVisibleColumns] = useState(() =>
    TOGGLEABLE_COLUMNS.reduce((acc, column) => {
      acc[column.key] = column.defaultVisible;
      return acc;
    }, {})
  );
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // Form state
  const [formSerial, setFormSerial] = useState('');
  const [formNickname, setFormNickname] = useState('');
  const [formInstallDate, setFormInstallDate] = useState('');

  const showMessage = useCallback((msg, isError = false) => {
    setMessage({ text: msg, isError });
    setTimeout(() => setMessage(null), isError ? 5000 : 3000);
  }, []);

  const fetchMachines = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllMachines();
      const machines = (response.data || []).map(machine => ({
        ...machine,
        // Map backend snake_case fields to the column keys used by the table.
        ntripUser: machine.ntrip_user ?? '',
        companyName: machine.company_name ?? '',
        installDate: machine.install_date ?? '',
        gnssVersion: machine.gnss_version ?? '',
      }));
      setMachines(machines);
    } catch (error) {
      console.error('Error fetching machines:', error);
      showMessage('Error fetching machines', true);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const toggleColumn = key => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSort = key => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortedMachines = () => {
    const sorted = [...machines];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, {
          numeric: true,
        });
      }

      return direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  const sortIndicator = key => {
    if (sortConfig.key !== key) {
      return '';
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const fetchAvailableSerials = useCallback(
    async (options = {}) => {
      const { notifyOnError = false, preserveSelection = true } = options;
      try {
        const response = await getAvailableMachineSerials();
        const serials = response.data?.serials || [];
        setAvailableSerials(serials);

        if (!preserveSelection) {
          setFormSerial(serials[0] || '');
        } else {
          setFormSerial(prev => {
            if (prev && serials.includes(prev)) {
              return prev;
            }
            return serials[0] || '';
          });
        }

        return serials;
      } catch (error) {
        console.error('Error fetching available serial numbers:', error);
        if (notifyOnError) {
          showMessage('Error loading available serial numbers', true);
        }
        return [];
      }
    },
    [showMessage]
  );

  useEffect(() => {
    fetchMachines();
    fetchAvailableSerials();
  }, [fetchAvailableSerials, fetchMachines]);

  const handleAddMachine = async () => {
    if (!formSerial.trim() || !formNickname.trim()) {
      showMessage('Please fill in both serial and nickname', true);
      return;
    }

    try {
      setLoading(true);
      await createMachine({
        serial: formSerial.trim(),
        nickname: formNickname.trim(),
      });
      showMessage('Machine created successfully!');
      setFormSerial('');
      setFormNickname('');
      setShowAddForm(false);
      fetchMachines();
      fetchAvailableSerials();
    } catch (error) {
      console.error('Error creating machine:', error);
      showMessage(error.response?.data?.detail || 'Error creating machine', true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMachine = async () => {
    if (!editingMachine || !formSerial.trim() || !formNickname.trim() || !formInstallDate) {
      showMessage('Please fill in serial, nickname and install date', true);
      return;
    }

    try {
      setLoading(true);
      await updateMachine(editingMachine.id, {
        serial: formSerial.trim(),
        nickname: formNickname.trim(),
        install_date: formInstallDate,
      });
      showMessage('Machine updated successfully!');
      setEditingMachine(null);
      setFormSerial('');
      setFormNickname('');
      setFormInstallDate('');
      fetchMachines();
      fetchAvailableSerials();
    } catch (error) {
      console.error('Error updating machine:', error);
      showMessage(error.response?.data?.detail || 'Error updating machine', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMachine = async machineId => {
    try {
      setLoading(true);
      await deleteMachine(machineId);
      showMessage('Machine deleted successfully!');
      setPendingDeleteMachine(null);
      fetchMachines();
      fetchAvailableSerials();
    } catch (error) {
      console.error('Error deleting machine:', error);
      showMessage('Error deleting machine', true);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = machine => {
    setPendingDeleteMachine(null);
    setEditingMachine(machine);
    setFormSerial(machine.serial);
    setFormNickname(machine.nickname);
    setFormInstallDate(machine.installDate);
    setShowAddForm(false);
  };

  const closeEditor = () => {
    if (loading) {
      return;
    }
    setEditingMachine(null);
    setShowAddForm(false);
    setFormSerial('');
    setFormNickname('');
    setFormInstallDate('');
  };

  const toggleAddForm = async () => {
    const nextShow = !showAddForm;
    setEditingMachine(null);

    if (nextShow) {
      setShowAddForm(true);
      setFormNickname('');
      setFormInstallDate('');
      await fetchAvailableSerials({ notifyOnError: true, preserveSelection: false });
    } else {
      setShowAddForm(false);
      setFormSerial('');
      setFormNickname('');
      setFormInstallDate('');
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div>
          <p>Fleet registry</p>
          <h1>Machines</h1>
          <span>Register hubs, maintain machine identities and review fleet assignments.</span>
        </div>
        <button onClick={toggleAddForm} className={styles.addButton} disabled={loading}>
          {showAddForm ? <X size={17} /> : <Plus size={17} />}
          {showAddForm ? 'Close form' : 'Add machine'}
        </button>
      </header>

      {message && (
        <Toast tone={message.isError ? 'error' : 'success'} onClose={() => setMessage(null)}>
          {message.text}
        </Toast>
      )}

      <ConfirmDialog
        open={!!pendingDeleteMachine}
        title={`Delete ${pendingDeleteMachine?.nickname || pendingDeleteMachine?.serial}?`}
        description="The machine and its related notes will be permanently removed. This cannot be undone."
        confirmLabel="Delete machine"
        busyLabel="Deleting…"
        busy={loading}
        onConfirm={() => handleDeleteMachine(pendingDeleteMachine.id)}
        onCancel={() => setPendingDeleteMachine(null)}
      />

      {/* Machines List */}
      <section className={styles.machinesSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Machine registry</h2>
            <span>{machines.length} machines</span>
          </div>
          <details className={styles.columnMenu}>
            <summary>
              <Columns3 size={16} />
              Columns
            </summary>
            <div className={styles.columnFilters}>
              {TOGGLEABLE_COLUMNS.map(column => (
                <label key={column.key} className={styles.columnFilterItem}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => toggleColumn(column.key)}
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </details>
        </div>

        {loading && machines.length === 0 ? (
          <div className={styles.loadingMessage}>Loading machines...</div>
        ) : machines.length === 0 ? (
          <div className={styles.emptyMessage}>No machines found. Add one to get started!</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.machinesTable}>
              <thead>
                <tr>
                  {TOGGLEABLE_COLUMNS.filter(column => visibleColumns[column.key]).map(column => (
                    <th
                      key={column.key}
                      className={styles.sortableHeader}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                      {sortIndicator(column.key)}
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedMachines().map(machine => (
                  <tr key={machine.id}>
                    {TOGGLEABLE_COLUMNS.filter(column => visibleColumns[column.key]).map(column => (
                      <td key={column.key}>{machine[column.key]}</td>
                    ))}
                    <td className={styles.actionsCell}>
                      <button
                        onClick={() => startEdit(machine)}
                        className={styles.editButton}
                        disabled={loading}
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setPendingDeleteMachine(machine)}
                        className={styles.deleteButton}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ActionDrawer
        open={showAddForm || Boolean(editingMachine)}
        eyebrow="Machine registry"
        title={editingMachine ? `Edit ${editingMachine.nickname}` : 'Add machine'}
        description={
          editingMachine
            ? 'Update the hub identity used throughout the fleet workspace.'
            : 'Pair an available hub serial number with a recognizable machine name.'
        }
        onClose={closeEditor}
        closeDisabled={loading}
        footer={
          <>
            <button onClick={closeEditor} disabled={loading} className={styles.cancelButton}>
              Cancel
            </button>
            <button
              onClick={editingMachine ? handleUpdateMachine : handleAddMachine}
              disabled={loading || (!editingMachine && availableSerials.length === 0)}
              className={styles.saveButton}
            >
              {loading
                ? editingMachine
                  ? 'Saving…'
                  : 'Creating…'
                : editingMachine
                  ? 'Save changes'
                  : 'Create machine'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          {editingMachine && (
            <div className={styles.drawerContext}>
              <span>Machine ID</span>
              <strong>{editingMachine.id}</strong>
              <span>Company</span>
              <strong>{editingMachine.companyName || 'Not assigned'}</strong>
            </div>
          )}
          <label className={styles.drawerField} htmlFor="machine-serial">
            <span>Serial number</span>
            {editingMachine ? (
              <input
                id="machine-serial"
                type="text"
                value={formSerial}
                onChange={event => setFormSerial(event.target.value)}
                placeholder="Enter hub serial number"
                className={styles.input}
                disabled={loading}
              />
            ) : (
              <select
                id="machine-serial"
                value={formSerial}
                onChange={event => setFormSerial(event.target.value)}
                className={styles.input}
                disabled={loading || availableSerials.length === 0}
              >
                <option value="" disabled>
                  {availableSerials.length === 0
                    ? 'No available serial numbers'
                    : 'Select a serial number'}
                </option>
                {availableSerials.map(serial => (
                  <option key={serial} value={serial}>
                    {serial}
                  </option>
                ))}
              </select>
            )}
            <small>The serial number identifies the connected hub.</small>
          </label>
          <label className={styles.drawerField} htmlFor="machine-nickname">
            <span>Machine nickname</span>
            <input
              id="machine-nickname"
              type="text"
              value={formNickname}
              onChange={event => setFormNickname(event.target.value)}
              placeholder="e.g. North field tractor"
              className={styles.input}
              disabled={loading}
            />
            <small>Use a name operators can recognize in maps, incidents and notes.</small>
          </label>
          {editingMachine && (
            <label className={styles.drawerField} htmlFor="machine-install-date">
              <span>Install date</span>
              <input
                id="machine-install-date"
                type="date"
                value={formInstallDate}
                onChange={event => setFormInstallDate(event.target.value)}
                className={styles.input}
                disabled={loading}
                required
              />
            </label>
          )}
        </div>
      </ActionDrawer>
    </div>
  );
}

export default MachineMgr;
